// 1️⃣ Estimate video/audio length in seconds from character count
export function estimateVideoLengthInSeconds(characters: number): number {
  if (characters <= 0) return 0;

  const averageCharactersPerSecond = 12.5; // ~150 wpm
  const estimatedSeconds = characters / averageCharactersPerSecond;

  return parseFloat(estimatedSeconds.toFixed(2)); // Round to 2 decimals
}

// 2️⃣ Calculate credits cost for a single video based on snapped/scaled time
export function calculateCredits(videoLengthInSeconds: number): number {
  if (videoLengthInSeconds <= 0) return 0;

  // Snap up to nearest 5 seconds, minimum 5s
  let roundedSeconds = Math.max(Math.ceil(videoLengthInSeconds / 5) * 5, 5);

  // Cap at 60 seconds
  roundedSeconds = Math.min(roundedSeconds, 60);

  if (roundedSeconds <= 15) {
    // Fixed price for 0–15s
    return parseFloat((4.41667).toFixed(5));
  } else if (roundedSeconds <= 30) {
    // Scale from 4.41667 to 6.625 over 15–30s
    const progress = (roundedSeconds - 15) / (30 - 15);
    return parseFloat((4.41667 + progress * (6.625 - 4.41667)).toFixed(5));
  } else {
    // Scale from 6.625 to 13.25 over 30–60s
    const progress = (roundedSeconds - 30) / (60 - 30);
    return parseFloat((6.625 + progress * (13.25 - 6.625)).toFixed(5));
  }
}

// 3️⃣ Estimate total credits for a batch (character count + number of avatars)
export function estimateCreditsTotal(
  characters: number,
  numberOfAvatars: number
): number {
  if (numberOfAvatars <= 0) return 0;

  const lengthInSeconds = estimateVideoLengthInSeconds(characters);
  const creditsPerVideo = calculateCredits(lengthInSeconds);

  const totalCredits = creditsPerVideo * numberOfAvatars;

  return parseFloat(totalCredits.toFixed(5));
}

export default function StickyBar({
  selectedActors,
  scriptText,
  handleGenerateVideo,
  handleNextForMergedImages,
  isProcessing,
  user,
  isOverLimit,
  allActorsHaveMergedImages,
  itemType,
}: {
  selectedActors: any[];
  scriptText: string;
  handleGenerateVideo: () => void;
  handleNextForMergedImages: () => void;
  isProcessing: boolean;
  user: any;
  isOverLimit: boolean;
  allActorsHaveMergedImages: boolean;
  itemType: string;
}) {
  return (
    <div className="fixed bottom-0 inset-x-0 ml-[inherit] z-40">
      {/* This wrapper ensures the bar aligns with the main content and respects the sidebar width */}
      <div
        className="ml-auto"
        style={{ width: "calc(100% - var(--sidebar-width, 0px))" }}
      >
        <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 shadow-lg py-3 px-2 transition-colors duration-200">
          <div className="flex justify-between items-center mx-auto px-2">
            <div className="flex items-center">
              {/* {user && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">{user.videos_left}</span>{" "}
                      credits available
                    </div>
                  )} */}
            </div>

            <div className="flex space-x-5 items-center">
              {selectedActors.length > 0 && (
                <span className="text-purple-800 dark:text-purple-400 font-medium">
                  {/* {estimateCredits(
                        scriptText.length,
                        true,
                        selectedActors.length
                      )}{" "} */}
                  ~
                  {parseFloat(
                    estimateCreditsTotal(
                      scriptText.length,
                      selectedActors.length
                    ).toFixed(2)
                  )}{" "}
                  {/* {estimateCreditsAndCost(
                    scriptText.length,
                    true,
                    selectedActors.length
                  )}{" "} */}
                  credits
                </span>
              )}
              {itemType === "product" && !allActorsHaveMergedImages ? (
                // Next button for when merged images are needed
                <button
                  onClick={handleNextForMergedImages}
                  disabled={selectedActors.length === 0 || isProcessing}
                  className={`${
                    selectedActors.length === 0 || isProcessing
                      ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-b from-blue-700 to-blue-500 hover:cursor-pointer border-2 border-blue-800 border-b-2 dark:from-blue-600 dark:to-blue-700 dark:hover:from-blue-700 dark:hover:to-blue-800"
                  } text-white font-semibold px-4 py-2.5 rounded-lg flex items-center transition-all duration-200 shadow-md`}
                >
                  {isProcessing ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>
                      Next: Apply Product Images
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 ml-2"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </>
                  )}
                </button>
              ) : (
                // Generate Video button for normal flow
                <button
                  onClick={handleGenerateVideo}
                  disabled={
                    !user ||
                    user.videos_left < selectedActors.length ||
                    isOverLimit ||
                    selectedActors.length === 0 ||
                    isProcessing
                  }
                  className={`${
                    !user ||
                    user.videos_left < selectedActors.length ||
                    isOverLimit ||
                    selectedActors.length === 0 ||
                    isProcessing
                      ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-b from-purple-700 to-purple-500 hover:cursor-pointer border-2 border-purple-800 border-b-2 dark:from-purple-600 dark:to-purple-700 dark:hover:from-purple-700 dark:hover:to-purple-800"
                  } text-white font-semibold px-4 py-2.5 rounded-lg flex items-center transition-all duration-200 shadow-md`}
                >
                  {isProcessing ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    <>Generate Now</>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
